import { z } from "zod";
import { SubscriptionSchema } from "./payment.js";
// Enum for user roles
export const UserRoleEnum = z.enum(["STUDENT", "ADMIN", "INSTRUCTOR", "CURATOR"]);
// Schema for language information
export const LanguageInfoSchema = z.object({
    code: z.string().min(2).max(5),
    name: z.string(),
    native_name: z.string(),
});
// Schema for user profile (embedded in user document in DynamoDB)
export const UserProfileSchema = z.object({
    native_language_code: z.string().min(2).max(5).nullable().optional(),
    target_language_code: z.string().min(2).max(5).nullable().optional(),
    learning_goal: z.string().nullable().optional(),
    timezone: z.string().nullable().optional(),
    level: z.enum(["A1", "A2", "B1", "B2", "C1", "C2"]).nullable().optional(),
    daily_goal_minutes: z.number().nullable().optional(),
    target_retention: z.number().min(0.70).max(0.97).nullable().optional(),
    // Enhanced profile with language info when requested
    native_language: LanguageInfoSchema.nullable().optional(),
    target_language: LanguageInfoSchema.nullable().optional(),
});
// Schema for tour/onboarding state (persisted on user record)
export const TourStateSchema = z.object({
    tour_completed: z.boolean().default(false),
    seen_tooltips: z.array(z.string()).default([]),
    checklist_dismissed: z.boolean().default(false),
});
// Schema for user stats (pre-computed aggregates in DynamoDB)
export const UserStatsSchema = z.object({
    courses_enrolled: z.number().min(0).default(0),
    courses_completed: z.number().min(0).default(0),
    exercises_completed: z.number().min(0).default(0),
    total_time_spent: z.number().min(0).default(0), // seconds
    average_score: z.number().min(0).max(100).default(0),
});
// Base User schema for DynamoDB
export const UserSchema = z.object({
    id: z.number(), // Generated ID (not auto-increment)
    uid: z.string(), // Cognito/Firebase UID - primary identifier
    email: z.string().email().nullable(),
    role: UserRoleEnum.default("STUDENT"),
    name: z.string().nullable(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    date_of_birth: z.string().nullable().optional(), // ISO date string "YYYY-MM-DD"
    avatar: z.string().nullable(),
    email_verified: z.boolean().default(false),
    last_login: z.date().nullable(),
    login_count: z.number().int().default(0),
    is_active: z.boolean().default(true),
    preferred_language: z.string().min(2).max(5).default("en"),
    created_at: z.date(),
    updated_at: z.date(),
    onboarding_completed: z.boolean().default(true),
    // Embedded profile (DynamoDB denormalization)
    profile: UserProfileSchema.optional(),
    // Tour/onboarding state
    tour_state: TourStateSchema.optional(),
    // Pre-computed stats (DynamoDB denormalization)
    stats: UserStatsSchema.optional(),
    // Subscription data
    subscription: SubscriptionSchema.optional(),
    // Curator assignment (UID of the curator assigned to this user)
    curator_uid: z.string().nullable().optional(),
    // Auth provider: "email" for email/password, "google" for Google-only
    auth_provider: z.enum(["email", "google"]).optional(),
    // Whether the user has any exercise activity (populated by list endpoint)
    has_activity: z.boolean().optional(),
});
// User with stats (returned when includeStats is true)
export const UserWithStatsSchema = UserSchema.extend({
    stats: UserStatsSchema,
});
// Payload schema for creating users
export const UserPayloadSchema = z.object({
    uid: z.string(),
    email: z.string().email().nullable().optional(),
    role: UserRoleEnum.optional(),
    name: z.string().nullable().optional(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    date_of_birth: z.string().nullable().optional(),
    avatar: z.string().nullable().optional(),
    email_verified: z.boolean().optional(),
    is_active: z.boolean().optional(),
    preferred_language: z.string().min(2).max(5).optional(),
    onboarding_completed: z.boolean().optional(),
    profile: UserProfileSchema.optional(),
    tour_state: TourStateSchema.optional(),
});
// Schema for user learning progress (per course)
export const UserLearningProgressSchema = z.object({
    course_id: z.number(),
    course_name: z.string().nullable(),
    progress_percentage: z.number().min(0).max(100),
    exercises_completed: z.number().min(0),
    total_time_spent: z.number().min(0), // seconds
    last_activity: z.date().nullable(),
    completed_at: z.date().nullable(),
});
// Schema for platform user statistics (admin dashboard)
export const PlatformUserStatsSchema = z.object({
    total_users: z.number().min(0),
    active_users: z.number().min(0),
    verified_users: z.number().min(0),
    users_by_role: z.array(z.object({
        role: z.string(),
        user_count: z.number().min(0),
    })),
    users_by_language: z.array(z.object({
        language_code: z.string(),
        language_name: z.string(),
        user_count: z.number().min(0),
    })),
    new_users_last_30_days: z.number().min(0),
    active_users_last_7_days: z.number().min(0),
});
// Schema for target language learner statistics
export const TargetLanguageLearnerStatsSchema = z.object({
    total_learners: z.number().min(0),
    active_learners: z.number().min(0),
    average_daily_goal: z.number().min(0),
    learners_by_native_language: z.array(z.object({
        native_language: z.string(),
        learner_count: z.number().min(0),
    })),
});
// Schema for user profile completeness
export const UserProfileCompletenessSchema = z.object({
    completeness_percentage: z.number().min(0).max(100),
    missing_fields: z.array(z.string()),
    suggestions: z.array(z.string()),
});
// Schema for user engagement metrics
export const UserEngagementMetricsSchema = z.object({
    days_active: z.number().min(0),
    total_sessions: z.number().min(0),
    avg_session_duration_minutes: z.number().min(0),
    exercises_per_day: z.number().min(0),
    streak_days: z.number().min(0),
    preferred_study_hours: z.array(z.object({
        hour: z.number().min(0).max(23),
        session_count: z.number().min(0),
    })),
});
// Schema for filters used in queries
export const UserFiltersSchema = z
    .record(z.string(), z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.date(),
    z.array(z.union([z.string(), z.number(), z.boolean()])),
]))
    .optional();
// Schema for bulk role updates
export const BulkUserRoleUpdateSchema = z.object({
    uid: z.string(),
    role: UserRoleEnum,
});
// Schema for user search parameters
export const UserSearchParamsSchema = z.object({
    searchTerm: z.string().min(1),
    limit: z.number().min(1).max(100).default(50),
    includeStats: z.boolean().default(true),
});
// Schema for user language update
export const UserLanguageUpdateSchema = z.object({
    uid: z.string(),
    languageCode: z.string().min(2).max(5),
});
// Schema for user role update
export const UserRoleUpdateSchema = z.object({
    uid: z.string(),
    role: UserRoleEnum,
});
// Schema for user registration
export const UserRegistrationSchema = z.object({
    uid: z.string(),
    email: z.string().email().nullable(),
    name: z.string().nullable(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    date_of_birth: z.string().nullable().optional(),
    avatar: z.string().nullable().optional(),
    preferred_language: z.string().min(2).max(5).optional(),
    profile: UserProfileSchema.optional(),
});
// Schema for user profile update
export const UserProfileUpdateSchema = z.object({
    name: z.string().nullable().optional(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    date_of_birth: z.string().nullable().optional(),
    email: z.string().email().nullable().optional(),
    avatar: z.string().nullable().optional(),
    preferred_language: z.string().min(2).max(5).optional(),
    onboarding_completed: z.boolean().optional(),
    profile: UserProfileSchema.nullable().optional(),
});
